import React, { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { FormComponentProps } from '@lmt/unicorn-shared/app/components/global-forms/GlobalForms.types';
import { IconButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/IconButton';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import TrashIcon from '@lmt/unicorn-shared/app/icons/trash.svg?react';
import UploadFileIcon from '@lmt/unicorn-shared/app/icons/upload-file.svg?react';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useQueryClient } from '@tanstack/react-query';
import { QueryFactory } from '@lmt/shared-types';

import styles from './ImportGDTFile.module.scss';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { readFileAsString } from '@lmt/unicorn-shared/app/helpers/file-helper';
import { useModal } from '../../common/ui-kit/modal/useModal';
import { MeasurementActions } from '@lmt/unicorn-shared/app/features/measurements/reducer';

const MAX_SIZE = 104857600;

export const ImportGDTFile: React.FC<FormComponentProps> = (props) => {
  const { getRootProps, getInputProps, open, acceptedFiles } = useDropzone({
    // Disable click and keydown behavior
    noClick: true,
    noKeyboard: true,
    accept: { gdt: ['.gdt'] },
  });
  const i18next = useScopedTranslation('tabs.gdt.import');
  const queryClient = useQueryClient();
  const { formType, formId, tryCompleteForm } = props;
  const dispatch = useUniDispatch();
  const modals = useModal();
  const [gdtFile, setGdtFile] = useState<File | null>(null);
  const [isSaving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    dispatch(MeasurementActions.pdfFileLoaded(null));
  }, []);

  useEffect(() => {
    updateDocuments();
  }, [acceptedFiles]);

  const updateDocuments = useCallback(() => {
    for (const file of acceptedFiles) {
      if (file.path?.toLowerCase().endsWith('.gdt')) {
        setGdtFile(file as File);
      }
    }
  }, [gdtFile, acceptedFiles]);

  const onCancel = useCallback(async () => {
    await tryCompleteForm(formType, formId, async () => undefined);
  }, [formType, formId, tryCompleteForm]);

  const onSave = useCallback(async () => {
    if (!gdtFile) {
      return;
    }
    setSaving(true);

    await tryCompleteForm(formType, formId, async () => {
      const fileContents = await readFileAsString(gdtFile);
      try {
        if (fileContents) {
          await QueryFactory.GDTIncomingMessageClient.receive(fileContents);
          await queryClient.invalidateQueries({
            queryKey: QueryFactory.GDTMessageQuery.getMessagesQueryKey(),
          });
        }
      } catch (e) {
        await modals.showError({ text: convertToErrorString(e) });
      } finally {
        setSaving(false);
      }
    });
  }, [formType, formId, tryCompleteForm, dispatch, gdtFile]);

  return (
    <div className={styles.container} data-test-id="importDocumentPopup">
      <div {...(getRootProps({ className: styles.dropzone }) as any)}>
        <input {...(getInputProps() as any)} multiple={false} accept=".gdt" />
        <UploadFileIcon className={styles.uploadIcon} />
        <div className={styles.text}>{i18next.t('import_text')}</div>
        <Button
          color={ButtonColor.Primary}
          title={i18next.t('upload_button')}
          onClick={open}
          className={styles.uploadButton}
        />
      </div>
      <div className={styles.documentsList}>
        <div className={styles.firstTitle}>
          {'Document'}
          <span className={styles.ext}>{'.gdt'}</span>
        </div>
        {gdtFile ? (
          <div className={styles.documentItem}>
            <div className={styles.fileName}>{gdtFile.name}</div>
            <div className={styles.options}>
              {gdtFile.size > MAX_SIZE && (
                <div className={styles.fileErrorText}>
                  {i18next.t('file_size_limit')}
                </div>
              )}
              <IconButton
                color="destroy"
                iconComponent={<TrashIcon className={styles.deleteIcon} />}
                onClick={() => setGdtFile(null)}
              />
            </div>
          </div>
        ) : (
          <div className={styles.empty}>{i18next.t('no_data')}</div>
        )}
      </div>
      <div className={styles.buttonContainer}>
        <Button
          className={styles.button}
          onClick={onCancel}
          color={ButtonColor.Primary}
          title={i18next.t('cancel_button')}
        />
        <Button
          disabled={!gdtFile || isSaving || gdtFile.size > MAX_SIZE}
          title={i18next.t('import_button')}
          className={styles.button}
          onClick={onSave}
        />
      </div>
    </div>
  );
};
